import { AriaAttributes, forwardRef, ReactElement } from "react";
import { classNames } from "../utils/classNames";
import { Spinner } from "./LoadingIndicator";
import { Tag } from "./Tag";
import { Icon, IconProps } from "./Icon";
import { renderIcon } from "./iconHelpers";
import { IconName } from "./iconMapping";
import { Box } from "./Box";
import { isTMessage, TMessage, TMessageParams } from "../i18n/tt";
import {
  DCFocusEventHandler,
  DCMouseEventHandler,
  useFocusEventHandler,
  useMouseEventHandler,
  useTranslation,
} from "../stores/RootStoreContext";
import { logWarn } from "../utils/log";
import { observer } from "mobx-react";
import { TMessageKey } from "../i18n/types.gen";

export type ButtonProps = {
  type?: "submit" | "button";
  variant: "primary" | "secondary" | "basic";
  color?: "primary" | "error";
  size?: "md" | "sm" | "xs";
  label?: ReactElement | TMessage | string | number;
  labelKey?: TMessageKey;
  labelParams?: TMessageParams;
  name?: string;
  icon?: ReactElement | IconProps["name"] | IconProps;
  arrow?: "left" | "right" | "up" | "down" | "out";
  loading?: boolean;
  disabled?: boolean;
  /** Buttons are full width by default on mobile, which can be overridden here */
  fullWidth?: boolean;
  onClick?: DCMouseEventHandler<HTMLButtonElement>;
  className?: string;
  tooltip?: TMessage | string;
} & AriaAttributes;

const iconSizeClasses = "tw-text-xl tw-leading-5 tw-h-5";

const renderButtonIcon = ({
  loading,
  icon,
  className,
}: {
  loading?: boolean;
  icon?: ReactElement | IconProps["name"] | IconProps;
  className?: string;
}) =>
  loading || icon ? (
    <span className={classNames(className, iconSizeClasses)}>
      {loading ? <Spinner /> : icon ? renderIcon(icon) : undefined}
    </span>
  ) : null;

export const Button = observer(
  forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        className,
        type,
        variant,
        size = "md",
        label,
        labelKey,
        labelParams,
        name,
        icon,
        arrow,
        loading,
        fullWidth,
        onClick,
        color = "primary",
        tooltip,
        ...props
      },
      ref,
    ) => {
      if (!label && !tooltip) {
        logWarn("<Button> should include either a `label` or `tooltip`");
      }
      const { t } = useTranslation();
      const clickHandler = useMouseEventHandler<HTMLButtonElement>(onClick);
      const iconOnly = icon && !label;
      tooltip = typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined;
      return (
        <button
          ref={ref}
          {...props}
          aria-label={tooltip}
          data-tooltip={tooltip}
          name={name}
          type={type ?? "button"}
          className={classNames(
            className,
            "tw-whitespace-nowrap",
            "tw-items-center",
            "tw-justify-center",
            "tw-gap-2",
            "tw-min-w-fit",
            "tw-leading-tight",

            size === "xs" &&
              (iconOnly ? ["tw-px-1", "tw-py-1", "text-xs"] : ["tw-px-3", "tw-py-1", "text-xs"]),
            size === "sm" &&
              (iconOnly ? ["tw-px-2", "tw-py-2", "text-sm"] : ["tw-px-4", "tw-py-2", "text-sm"]),
            size === "md" &&
              (iconOnly
                ? ["tw-px-3", "tw-py-3", "text-base"]
                : ["tw-px-4", "tw-py-3", "text-base"]),

            fullWidth
              ? ["tw-flex", "tw-w-full"]
              : fullWidth === false
                ? ["tw-inline-flex", "tw-w-auto"]
                : ["tw-flex", "tw-w-full", "md:tw-inline-flex", "md:tw-w-auto"],

            variant === "primary" && [
              color === "primary"
                ? "bg-primary hover:bg-primary-dark color-primary-contrast"
                : color === "error"
                  ? "bg-accent2 color-accent2-contrast"
                  : undefined,
              "tw-font-semibold",
              "tw-border",
              "border-transparent",
              "disabled:bg-gray-hard-dimmed10",
              "disabled:subtle",
              "tw-rounded",
            ],

            variant === "secondary" && [
              "tw-border",
              "bg-primary-contrast",
              "tw-font-semibold",
              color === "primary"
                ? [
                    "color-primary",
                    "border-primary",
                    "hover:border-primary-dark",
                    "hover:color-primary-dark",
                  ]
                : color === "error"
                  ? "color-error border-error"
                  : undefined,
              "disabled:bg-gray-hard-dimmed10",
              "disabled:border-transparent",
              "disabled:subtle",
              "disabled:hover:subtle",
              "tw-rounded",
            ],

            variant === "basic" && [
              "tw-rounded",
              "tw-border",
              "border-gray",
              "color-default",
              "bg-primary-contrast",
              "hover:border-primary",
              "disabled:bg-gray-hard-dimmed10",
              "disabled:subtle",
              "disabled:border-transparent",
            ],
          )}
          onClick={loading ? undefined : clickHandler}
        >
          {arrow === "left" && (
            <span className={iconSizeClasses}>
              <Icon name={"arrow-left"} weight={"bold"} />
            </span>
          )}
          {renderButtonIcon({ loading, icon })}
          {labelKey ? (
            <span>{t(labelKey, labelParams)}</span>
          ) : label ? (
            <span>{isTMessage(label) ? t(label) : label}</span>
          ) : undefined}
          {(arrow === "right" || arrow === "up" || arrow === "down" || arrow === "out") && (
            <span className={iconSizeClasses}>
              <Icon
                name={
                  arrow === "right"
                    ? "arrow-right"
                    : arrow === "up"
                      ? "caret-up"
                      : arrow === "down"
                        ? "caret-down"
                        : "arrow-square-out"
                }
                weight={arrow === "out" ? "regular" : "bold"}
              />
            </span>
          )}
        </button>
      );
    },
  ),
);

export type LinkButtonProps = {
  label: string | TMessage;
  type?: "button" | "submit";
  icon?: ReactElement | IconProps["name"] | IconProps;
  size?: "sm" | "xs";
  arrow?: "left" | "right" | "down" | "up" | "out" | false;
  loading?: boolean;
  disabled?: boolean;
  underline?: boolean;
  color?: "inherit" | "primary" | "error";
  weight?: "semibold" | "normal";
  className?: string;
  onClick?: DCMouseEventHandler<HTMLButtonElement>;
  tooltip?: string;
  testId?: string;
  truncate?: boolean;
} & AriaAttributes;

export const LinkButton = observer(
  forwardRef<HTMLButtonElement, LinkButtonProps>(
    (
      {
        label,
        type = "button",
        icon,
        size,
        arrow,
        loading,
        disabled,
        color = "primary",
        className,
        underline,
        weight = "semibold",
        tooltip,
        onClick,
        testId,
        truncate,
        ...props
      },
      ref,
    ) => {
      const { t } = useTranslation();
      const clickHandler = useMouseEventHandler<HTMLButtonElement>(onClick);
      return (
        <button
          {...props}
          ref={ref}
          aria-label={tooltip}
          data-tooltip={tooltip}
          data-testid={testId}
          type={type ?? "button"}
          disabled={disabled}
          className={classNames(
            className,
            weight === "semibold" ? "tw-font-semibold" : "tw-font-normal",
            "tw-items-center",
            "tw-justify-center",
            "tw-inline-flex",
            "tw-w-auto",
            "tw-gap-1",
            !disabled && "hover:tw-underline",
            disabled && "tw-opacity-50",
            underline && "tw-underline",
            color === "inherit"
              ? ["color-inherit", underline !== false && "tw-underline"]
              : color === "primary"
                ? "color-primary"
                : "color-error",
            size === "xs" ? ["tw-text-sm"] : size === "sm" ? ["tw-text-sm"] : ["tw-text-base"],
            truncate && "overflow-hidden",
          )}
          onClick={loading || disabled ? undefined : clickHandler}
        >
          {arrow === "left" && (
            <span className={"tw-text-xl tw-leading-5 tw-h-5"}>
              <Icon name={"caret-left"} weight={"bold"} />
            </span>
          )}
          {(icon || loading) && (
            <span className={"tw-text-xl tw-leading-5 tw-h-5"}>
              {loading ? <Spinner /> : icon ? renderIcon(icon) : undefined}
            </span>
          )}
          {label && (
            <span className={truncate ? "tw-truncate" : undefined}>
              {isTMessage(label) ? t(label) : label}
            </span>
          )}
          {arrow === "right" && (
            <span className={"tw-text-xl tw-leading-5 tw-h-5"}>
              <Icon name={"caret-right"} weight={"bold"} />
            </span>
          )}
          {arrow === "down" && (
            <span className={"tw-text-xl tw-leading-5 tw-h-5"}>
              <Icon name={"caret-down"} weight={"bold"} />
            </span>
          )}
          {arrow === "up" && (
            <span className={"tw-text-xl tw-leading-5 tw-h-5"}>
              <Icon name={"caret-up"} weight={"bold"} />
            </span>
          )}
          {arrow === "out" && (
            <span className={"tw-text-xl tw-leading-5 tw-h-5"}>
              <Icon name={"arrow-square-out"} color={"subtle"} />
            </span>
          )}
        </button>
      );
    },
  ),
);

export type IconButtonProps = {
  icon: ReactElement | IconProps["name"] | IconProps;
  tooltip: string | TMessage;
  type?: "submit" | "button";
  loading?: boolean;
  disabled?: boolean;
  color?: "primary" | "error" | "gray" | "inherit" | "accent3";
  onClick?: DCMouseEventHandler<HTMLButtonElement>;
  onFocus?: DCFocusEventHandler<HTMLButtonElement>;
  onBlur?: DCFocusEventHandler<HTMLButtonElement>;
  tabIndex?: number;
  compact?: boolean;
  className?: string;
  tag?: string;
} & AriaAttributes;

export const IconButton = observer(
  forwardRef<HTMLButtonElement, IconButtonProps>(
    (
      {
        icon,
        tooltip,
        type,
        loading,
        disabled,
        color = "primary",
        compact,
        className,
        tag,
        onClick,
        onFocus,
        onBlur,
        ...props
      },
      ref,
    ) => {
      const { t } = useTranslation();
      const tooltipText = typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined;
      const clickHandler = useMouseEventHandler<HTMLButtonElement>(onClick);
      const focusHandler = useFocusEventHandler<HTMLButtonElement>(onFocus);
      const blurHandler = useFocusEventHandler<HTMLButtonElement>(onBlur);
      return (
        <button
          {...props}
          ref={ref}
          onClick={disabled || loading ? undefined : clickHandler}
          onFocus={focusHandler}
          onBlur={blurHandler}
          aria-label={tooltipText}
          data-tooltip={tooltipText}
          type={type ?? "button"}
          disabled={disabled}
          className={classNames(
            className,
            "tw-inline-flex",
            "tw-items-center",
            "tw-font-semibold",
            !tag && "hover:tw-underline",
            "tw-text-xl",
            "tw-rounded-full",
            !compact && "tw-p-3",
            disabled && "tw-opacity-50",
            color === "inherit"
              ? "color-inherit"
              : color === "error"
                ? "color-error"
                : color === "gray"
                  ? "subtle"
                  : color === "accent3"
                    ? "color-accent3"
                    : "color-primary",
            !disabled &&
              (color === "error"
                ? "hover:bg-error"
                : color === "gray"
                  ? ["hover:bg-alt1", "hover:subtle"]
                  : color === "accent3"
                    ? ["hover:bg-accent3-light", "hover:color-accent3"]
                    : color === "primary"
                      ? ["hover:bg-primary-light", "hover:color-primary-dark"]
                      : ["hover:bg-currentcolor-dimmed"]),
          )}
        >
          {loading ? (
            <Spinner />
          ) : tag ? (
            <Box alignItems={"center"}>
              {renderIcon(icon)} <Tag compact label={tag} color={"error"} />
            </Box>
          ) : (
            renderIcon(icon)
          )}
        </button>
      );
    },
  ),
);

export type ToggleButtonProps = {
  testId?: string;
  selected?: boolean;
  icon?: IconName;
  label?: string;
  tooltip?: string | TMessage;
  loading?: boolean;
  disabled?: boolean;
  onClick?: DCMouseEventHandler<HTMLButtonElement>;
  variant?: "primary" | "basic" | "small";
  disableMinWidth?: boolean;
  grow?: boolean;
  grouped?: boolean;
} & AriaAttributes;

export const ToggleButton = observer(
  forwardRef<HTMLButtonElement, ToggleButtonProps>(
    (
      {
        testId,
        selected = false,
        icon,
        label,
        tooltip,
        loading,
        disabled,
        variant = "primary",
        onClick,
        disableMinWidth,
        grow,
        grouped,
        ...props
      },
      ref,
    ) => {
      const { t } = useTranslation();
      const clickHandler = useMouseEventHandler<HTMLButtonElement>(onClick);
      const tooltipText = !tooltip || typeof tooltip === "string" ? tooltip : t(tooltip);
      return (
        <button
          {...props}
          data-testid={testId}
          ref={ref}
          type={"button"}
          aria-label={label || tooltipText}
          data-tooltip={tooltipText}
          aria-pressed={selected}
          onClick={selected || disabled || loading ? undefined : clickHandler}
          disabled={disabled}
          className={classNames(
            "tw-flex",
            "tw-flex-row",
            "tw-gap-2",
            "tw-items-center",
            "tw-justify-center",
            "tw-leading-tight",
            grouped
              ? variant === "small"
                ? [
                    "tw-border",
                    "first:tw-rounded-l",
                    "last:tw-rounded-r",
                    !selected && "[&:not(:first-child):not(:hover)]:border-l-transparent",
                  ]
                : variant === "basic"
                  ? [
                      "tw-border",
                      "first:tw-rounded-l",
                      "last:tw-rounded-r",
                      "[&:not(:first-child):not(:hover)]:border-l-transparent",
                    ]
                  : [
                      "tw-border-y",
                      "tw-border-l",
                      "first:tw-rounded-l",
                      "last:tw-rounded-r",
                      "last:tw-border-r",
                    ]
              : ["tw-rounded", "tw-border"],
            disabled && "tw-opacity-50",
            grow && "tw-flex-grow",

            variant === "small" && [
              "tw-px-4 tw-py-2 text-xs",
              selected
                ? ["bg-primary-light", "border-primary"]
                : ["bg-gray-x-soft", "border-gray-soft", !disabled && "hover:border-primary"],
            ],

            variant === "primary" && [
              "tw-px-4 tw-py-3 text-base",
              "tw-font-semibold",
              !disableMinWidth && "tw-min-w-60",
              selected
                ? [
                    "bg-primary",
                    "color-primary-contrast",
                    "border-primary",
                    "hover:bg-primary-dark",
                    "hover:border-primary-dark",
                  ]
                : [
                    "bg-primary-contrast",
                    "color-primary",
                    "border-primary",
                    "hover:color-primary-dark",
                    "hover:border-primary-dark",
                  ],
            ],

            variant === "basic" && [
              "tw-px-4 tw-py-3 text-base",
              !disableMinWidth && "tw-min-w-60",
              selected
                ? [
                    "bg-primary-light",
                    "color-default",
                    "tw-font-semibold",
                    "border-gray",
                    "hover:border-gray-hard",
                  ]
                : ["bg-default", "color-default", "border-gray", "hover:border-gray-hard"],
            ],
          )}
        >
          {renderButtonIcon({
            loading,
            icon,
            className: variant === "basic" && selected ? "color-primary" : undefined,
          })}
          {label && !loading && <div>{label}</div>}
        </button>
      );
    },
  ),
);

export function BackButton({ onClick, name }: { onClick: () => void; name?: string }) {
  return (
    <Button
      fullWidth={false}
      variant={"basic"}
      icon={"caret-left"}
      tooltip={{ key: "back" }}
      onClick={onClick}
      name={name}
    />
  );
}

export const CloseButton = ({ onClick }: { onClick: IconButtonProps["onClick"] }) => {
  return <IconButton icon={"x"} tooltip={{ key: "close" }} onClick={onClick} />;
};
